import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import './EmployeeCostDetails.scss';
import { useAuth } from '../../../Hooks/useAuth';
import { buildEmployeeCostUserInfo } from './employeeCostUserInfo';

const ACCEPTED_EXTENSIONS = ['csv', 'xlsx'];
const isEmptyCellValue = (value) => value === null || value === undefined || String(value).trim() === '';
const hasRowData = (rowValues = []) => rowValues.some((cellValue) => !isEmptyCellValue(cellValue));

const buildUploadPayload = (uploadData, userProfile) => ({
  importData: { uploadData },
  userInfo: buildEmployeeCostUserInfo(userProfile),
});

const parseExcelRows = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const worksheet = workbook.worksheets[0];
  if (!worksheet) return null;

  const headerRow = (worksheet.getRow(1).values || [])
    .slice(1)
    .map((header) => String(header || '').trim());

  const uploadData = [];
  for (let rowIndex = 2; rowIndex <= worksheet.rowCount; rowIndex += 1) {
    const rowValues = (worksheet.getRow(rowIndex).values || []).slice(1);
    if (!hasRowData(rowValues)) continue;

    const rowObject = {};
    headerRow.forEach((header, idx) => {
      if (!header) return;
      const cellValue = rowValues[idx];
      rowObject[header] = isEmptyCellValue(cellValue) ? null : cellValue;
    });
    uploadData.push(rowObject);
  }

  return uploadData;
};

const parseCsvRows = (file) => new Promise((resolve, reject) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: (results) => {
      const uploadData = (results.data || []).filter((row) => row && hasRowData(Object.values(row)));
      resolve(uploadData);
    },
    error: reject,
  });
});

const uploadRows = async ({
  baseURL,
  headers,
  userProfile,
  uploadData,
  handleClose,
  onSuccess,
  setServerReport,
  setUploading,
}) => {
  try {
    const res = await fetch(`${baseURL}/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(buildUploadPayload(uploadData, userProfile)),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      if (data?.data) setServerReport(data.data);
      toast.error(data?.error || data?.message || 'Import completed with errors.');
      return;
    }

    toast.success(data?.message || 'File uploaded successfully.');
    handleClose();
    onSuccess?.();
  } catch (err) {
    toast.error('Upload failed. Please check your connection.');
  } finally {
    setUploading(false);
  }
};

const validateFile = (file) => {
  if (!file) return 'Please select a file.';
  const nameParts = file.name.split('.');
  const ext = nameParts.length ? nameParts[nameParts.length - 1].toLowerCase() : '';
  if (!ACCEPTED_EXTENSIONS.includes(ext)) {
    return `Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`;
  }
  return '';
};

const ErrorReport = ({ serverReport }) => {
  if (!serverReport) return null;
  return (
    <div className="employee-cost-error-report-container">
      <div className="employee-cost-error-header">Upload Issues:</div>
      {serverReport.message ? (
        <div className="employee-cost-error-summary">
          {serverReport.message}
        </div>
      ) : null}
      <ul className="employee-cost-error-list">
        {serverReport.validationErrors?.map((validationError, idx) => (
          <li key={idx}>{`Row ${validationError.row} , ${validationError.error}`}</li>
        ))}
        {serverReport.duplicates?.map((duplicate, idx) => (
          <li key={idx}>{`Row ${duplicate.row} , Duplicate`}</li>
        ))}
        {serverReport.invalidMasters?.map((invalidMaster, idx) => (
          <li key={idx}>{`Row ${invalidMaster.row} , ${invalidMaster.error || 'Invalid master value'}`}</li>
        ))}
      </ul>
    </div>
  );
};

ErrorReport.propTypes = {
  serverReport: PropTypes.shape({
    message: PropTypes.string,
    validationErrors: PropTypes.arrayOf(PropTypes.shape({
      row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      error: PropTypes.string,
    })),
    duplicates: PropTypes.arrayOf(PropTypes.shape({
      row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    invalidMasters: PropTypes.arrayOf(PropTypes.shape({
      row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      error: PropTypes.string,
    })),
  }),
};

ErrorReport.defaultProps = {
  serverReport: null,
};

const EmployeeCostUploadDialog = ({ open, onClose, baseURL, headers, onSuccess }) => {
  const { userProfile } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [serverReport, setServerReport] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setSelectedFile(null);
    setFileError('');
    setServerReport(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const error = validateFile(file);
    setFileError(error);
    setSelectedFile(error ? null : file);
    event.target.value = '';
  };

  const handleUpload = async () => {
    const error = validateFile(selectedFile);
    if (error) {
      setFileError(error);
      return;
    }

    setUploading(true);
    setServerReport(null);

    const ext = (selectedFile.name.split('.').pop() || '').toLowerCase();
    if (ext === 'xlsx') {
      parseExcelRows(selectedFile)
        .then((uploadData) => {
          if (!uploadData || !uploadData.length) {
            toast.error('Uploaded Excel file does not contain any data rows.');
            setUploading(false);
            return;
          }
          return uploadRows({
            baseURL,
            headers,
            userProfile,
            uploadData,
            handleClose,
            onSuccess,
            setServerReport,
            setUploading,
          });
        })
        .catch((parseErr) => {
          toast.error(`Excel parsing error: ${parseErr.message}`);
          setUploading(false);
        });
      return;
    }

    parseCsvRows(selectedFile)
      .then((uploadData) => {
        if (!uploadData.length) {
          toast.error('Uploaded CSV file does not contain any data rows.');
          setUploading(false);
          return;
        }
        return uploadRows({
          baseURL,
          headers,
          userProfile,
          uploadData,
          handleClose,
          onSuccess,
          setServerReport,
          setUploading,
        });
      })
      .catch((parseErr) => {
        toast.error(`CSV Parsing error: ${parseErr.message}`);
        setUploading(false);
      });
  };

  const downloadSample = async () => {
    try {
      const response = await fetch(`${baseURL}/get-sample-file`, {
        method: 'GET',
        headers: { ...headers },
      });

      if (!response.ok) {
        throw new Error('Failed to download sample file.');
      }

      const data = await response.json();
      const signedUrl = data?.data?.signedUrl;

      if (!signedUrl) {
        throw new Error('Download URL not found.');
      }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';

      document.body.append(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (err) {
      toast.error(err?.message || 'Failed to download sample file.');
    }
  };

  const dialogHeader = (
    <React.Fragment>
      <span>Upload Employee Cost Details</span>
      <button
        type="button"
        onClick={downloadSample}
        className="employee-cost-upload-sample-link"
      >
        Sample File
      </button>
    </React.Fragment>
  );

  const dialogFooter = (
    <div className="employee-cost-dialog-footer">
      <Button
        label="Cancel"
        className="p-button p-button-text"
        onClick={handleClose}
        disabled={uploading}
      />
      <Button
        label="Upload"
        className="p-button p-button-add"
        onClick={handleUpload}
        disabled={uploading || !selectedFile}
      />
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={dialogHeader}
      footer={dialogFooter}
      className="employee-cost-dialog"
      style={{ width: '480px' }}
      modal
      draggable={false}
      resizable={false}
      closable={false}
    >
      <div className="employee-cost-upload-body">
        <div className="employee-cost-upload-field-label">
          Select file to upload
        </div>

        <div className="employee-cost-upload-file-row">
          <Button
            type="button"
            label="Choose file"
            className="p-button employee-cost-upload-choose-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <span
            className="employee-cost-upload-filename"
            title={selectedFile ? selectedFile.name : ''}
          >
            {selectedFile ? selectedFile.name : 'No file chosen'}
          </span>
        </div>

        {fileError && (
          <div className="employee-cost-upload-error">{fileError}</div>
        )}

        <div className="employee-cost-upload-hint">
          Accepted formats: {ACCEPTED_EXTENSIONS.join(', ')}
        </div>

        <ErrorReport serverReport={serverReport} />
      </div>
    </Dialog>
  );
};

EmployeeCostUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
  headers: PropTypes.object,
  onSuccess: PropTypes.func,
};

EmployeeCostUploadDialog.defaultProps = {
  headers: {},
  onSuccess: null,
};

export default EmployeeCostUploadDialog;

